import Color from 'color'
import { once } from 'lodash'

type ColorString = string | null | undefined
type Alpha = number | undefined

/**
 * The CSS ‘relative color’ syntax (`color(from …)`) is in Baseline 2024, but
 * (as of 2024-11-25) isn’t guaranteed to be supported based on our
 * `browserslist` config. It’s also not supported in the current Jest version
 * (29.7.0).
 *
 * @see https://caniuse.com/css-relative-colors
 */
function testRelativeColorSupport() {
  const fragment = document.createElement('div')
  fragment.style.color = '#000'
  document.body.append(fragment)

  fragment.style.color = 'color(from #fff display-p3 r g b)'
  const supported = window.getComputedStyle(fragment).color !== 'rgb(0, 0, 0)'
  document.body.removeChild(fragment)

  return supported
}

declare global {
  interface Window {
    /** @private */
    CONTROLLED_RELATIVE_COLOR_SUPPORT?: boolean
    /** @private */
    USE_CONTROLLED_RELATIVE_COLOR_SUPPORT?: boolean
  }
}
const supportsRelativeColor = window.USE_CONTROLLED_RELATIVE_COLOR_SUPPORT
  ? once(testRelativeColorSupport)
  : () => window.CONTROLLED_RELATIVE_COLOR_SUPPORT

function reducePrecision(n: number): number {
  return Math.round(n * 1000) / 1000
}

function fallbackForColorFn(
  inputColor: NonNullable<ColorString>,
  alpha: Alpha
) {
  const fnParts = inputColor.match(
    /^(?<head>color\(\w[\w-]+(?:\s\d(?:\.\d+)?){3})(?:\s\/\s+(?<alpha>\d(?:\.\d+)?))?(?<tail>\))$/
  )
  if (!fnParts?.groups) return

  const opacityPart =
    (alpha ?? fnParts.groups.alpha) != undefined
      ? ` / ${alpha ?? fnParts.groups.alpha}`
      : ''
  return fnParts.groups.head + opacityPart + fnParts.groups.tail
}

/** @private Only exported for use in tests */
export function hexToColorFn(inputColor: NonNullable<ColorString>) {
  let color
  try {
    color = Color(inputColor)
  } catch {
    console.warn(
      `Unable to parse "${inputColor}"; skipping wide-gamut conversion`
    )
    return inputColor
  }

  const tidiedRGBComponents = color.unitArray().slice(0, 3).map(reducePrecision)
  const opacityPart =
    color.alpha() < 1
      ? ` / ${reducePrecision(color.unitObject().alpha ?? 1)}`
      : ''

  return `color(display-p3 ${tidiedRGBComponents.join(' ')}${opacityPart})`
}

const colorFunctionPattern = /^color\(.+\)$/

/**
 * Attempt to generate a CSS `color()` with the wide-gamut (Display P3) version
 * of `inputColor`. If `inputColor` can’t be parsed it’s returned as-is.
 *
 * Prefer using `Color` whenever possible, as all its values are automatically
 * wide-gamut; use `wideGamutColor` if you work directly with `GemColors`
 *
 * @note If provided, `alpha` overrides `inputColor`’s alpha, rather than
 * something like multiplying it — for example, `wideGamutColor("#FFF2", 0.5)`
 * produces a color with 50% opacity, replacing the original’s 13% opacity.
 * @note This uses the relative `color()` syntax (`color(from …)`) when
 * possible.
 *
 * @example
 * wideGamutColor(GemColors.N000)
 * //=> "color(display-p3 1 1 1)"
 * wideGamutColor(GemColors.N000, 0.5)
 * //=> "color(from #FFFFFF display-p3 r g b / 0.5)"
 * // = "color(display-p3 1 1 1 / 0.5)"
 * wideGamutColor('#ffcc0090')
 * //=> "color(display-p3 1 0.8 0 / 0.56)"
 * wideGamutColor('#ffcc0090', 0.5)
 * //=> "color(display-p3 1 0.8 0 / 0.5)"
 * wideGamutColor(Color.dark['Color/Primary/Primary-2'])
 * //=> "color(display-p3 0.965 0.976 0.996 / 0.5)"
 * wideGamutColor(Color.dark['Color/Primary/Primary-2'], 0.5)
 * //=> "color(from color(display-p3 0.965 0.976 0.996) display-p3 r g b / 0.5)"
 * // = "color(display-p3 0.965 0.976 0.996 / 0.5)"
 */
export function wideGamutColor(
  inputColor: ColorString,
  alpha: Alpha = undefined
): string {
  if (inputColor == null) return 'transparent'

  if (alpha) {
    if (!Number.isFinite(alpha))
      throw new TypeError('alpha must be a number in the interval [0,1]')
    if (alpha < 0 || alpha > 1)
      throw new RangeError('alpha must be in the interval [0,1]')
  }

  // No point wrapping a `color()` value if the alpha isn’t changing
  if (colorFunctionPattern.test(inputColor) && alpha == undefined)
    return inputColor

  // `color()`'s relative-color syntax can handle hex values, but hex values are
  // implicitly in the sRGB color space, and the `color()` output value will be
  // identical to the sRGB version. To make the color wide-gamut the sRGB
  // components need to be put in a different color space.
  const asColorFn = /^#[a-fA-F0-9]{3,8}/.test(inputColor)
    ? hexToColorFn(inputColor)
    : inputColor

  // Ideal case -- defers all computation to browser
  if (supportsRelativeColor())
    return `color(from ${asColorFn} display-p3 r g b / ${alpha ?? 'alpha'})`

  const fromColorFn = fallbackForColorFn(asColorFn, alpha)
  if (fromColorFn) return fromColorFn

  console.warn(
    `Unable to parse "${inputColor}"; skipping wide-gamut conversion`
  )

  return inputColor
}
